/**
 * <MediaThumbnail> — a single image cell (atom, L0.1).
 *
 * A rounded, fixed-size preview of a picked image (RN core `<Image>` from a `uri`). Optional press
 * (open the viewer) and an optional trash button anchored to the top-right corner (delete). The maison
 * replacement for the per-image cell the legacy `MaterialImage` hand-rolled.
 *
 * `IconButton` has no `style` prop, so the trash is positioned via an absolute wrapper `<View>`.
 * No hex literals — the delete affordance is the `danger`-filled `<IconButton>`.
 *
 * Trois états de plus, pour la copie que l'élève envoie photo par photo (tous absents par défaut :
 * la vignette d'avant) : `dimmed` l'estompe le temps qu'elle part, `failed` la cerne du rouge
 * d'erreur quand elle n'est pas partie, `moreCount` pose « +N » sur un voile quand la rangée
 * déborde.
 *
 * `video` (défaut `false`) : la vignette d'une vidéo jointe — une case unie et le glyphe de la vidéo,
 * puisqu'`<Image>` ne sait pas montrer la première image d'une vidéo.
 */
import React from "react"
import { Image, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { dimming } from "../../theme/tokens/effects"
import { IconButton } from "../Buttons/IconButton"
import { Icon } from "../icons/Icon"
import { Text } from "../typography/Text"

export type MediaThumbnailProps = {
    uri: string
    /** Square footprint in px — defaults to 100. */
    size?: number
    /**
     * `cover` (défaut, rendu inchangé) recadre pour remplir la vignette ; `contain` montre le
     * document ENTIER dans la boîte. Un aperçu de fichier déposé se lit en entier — une photo
     * d'exercice recadrée au centre ne dit plus de quel exercice il s'agit.
     */
    fit?: "cover" | "contain"
    /** Tap the image (e.g. open a full-screen viewer). */
    onPress?: () => void
    /** Shows a trash button overlay; called when tapped. */
    onDelete?: () => void
    /** L'image part encore : estompée (jeton `dimming`). Défaut `false`. */
    dimmed?: boolean
    /** L'image n'est pas partie : cernée du rouge d'erreur. Défaut `false`. */
    failed?: boolean
    /** Les images qui ne tiennent plus dans la rangée : « +N » sur un voile. Absent ou 0 : rien. */
    moreCount?: number
    /** Une vidéo : la case et son glyphe à la place de l'image. Défaut `false`. */
    video?: boolean
    testID?: string
    accessibilityLabel?: string
}

export function MediaThumbnail(props: MediaThumbnailProps) {
    const {
        uri,
        size = 100,
        fit = "cover",
        onPress,
        onDelete,
        dimmed = false,
        failed = false,
        moreCount = 0,
        video = false,
        testID,
        accessibilityLabel,
    } = props

    return (
        <Pressable
            onPress={onPress}
            disabled={!onPress}
            style={styles.wrap}
            testID={testID}
            accessibilityLabel={accessibilityLabel}
        >
            {video ? (
                <View style={[styles.img(size), styles.video, dimmed && styles.dimmed]}>
                    <Icon name="video" size={24} color="text-muted" />
                </View>
            ) : (
                <Image
                    source={{ uri }}
                    resizeMode={fit}
                    style={dimmed ? [styles.img(size), styles.dimmed] : styles.img(size)}
                />
            )}
            {failed && <View style={styles.failed(size)} pointerEvents="none" />}
            {moreCount > 0 && (
                <View style={styles.more(size)} pointerEvents="none">
                    <Text weight="bold" size="text-lg" color="white">
                        {`+${moreCount}`}
                    </Text>
                </View>
            )}
            {onDelete != null && (
                <View style={styles.trash}>
                    <IconButton
                        iconName="trash"
                        color="danger"
                        size={28}
                        iconSize={13}
                        onPress={onDelete}
                        accessibilityLabel="Supprimer"
                    />
                </View>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Don't stretch inside a column; keep the cell to the image footprint so the trash anchors correctly.
    wrap: {
        alignSelf: "flex-start" as const,
    },
    img: (size: number) => ({
        width: size,
        height: size,
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    }),
    dimmed: { opacity: dimming.opacity },
    // Sans image, la case se dessine par son contour : sur un encart, son fond s'y confondrait.
    video: {
        alignItems: "center" as const,
        justifyContent: "center" as const,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    failed: (size: number) => ({
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: theme.radius.md,
        borderWidth: 2,
        borderColor: theme.colors.danger,
    }),
    more: (size: number) => ({
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: theme.radius.md,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors.backdrop,
    }),
    trash: {
        position: "absolute" as const,
        top: -8,
        right: -8,
    },
}))
